import React, { useEffect, useMemo, useRef, useState } from 'react';
import DataTable from './components/DataTable.js';
import MapVeto from './components/MapVeto.js';
import OtterPeek from './components/OtterPeek.js';
import PlayerDetail from './components/PlayerDetail.js';
import Avatar from './components/Avatar.js';
import { averages, detailFor, enrichPlayers, initials, n1 } from './stats';
import socket, { onIdentity, requestState } from './config/socket';

const MUTED = '#6b7a68';

// Die Wiese am unteren Rand: ein Farbverlauf und zwei Halmreihen, die
// gegenlaeufig im Wind stehen.
const Meadow = () => (
  <div style={{ position: 'fixed', inset: 'auto 0 0 0', height: 190, pointerEvents: 'none', zIndex: 0 }}>
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background:
          'linear-gradient(180deg, rgba(146,190,110,0) 0%, rgba(126,177,92,0.55) 60%, rgba(96,150,68,0.85) 100%)',
      }}
    />
    <div
      style={{
        position: 'absolute',
        inset: 'auto 0 0 0',
        height: 74,
        background: 'repeating-linear-gradient(90deg, #5f9a41 0 3px, transparent 3px 11px)',
        maskImage: 'linear-gradient(180deg, transparent 0%, #000 72%)',
        WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 72%)',
        animation: 'sway 7s ease-in-out infinite',
        transformOrigin: 'bottom',
      }}
    />
    <div
      style={{
        position: 'absolute',
        inset: 'auto 0 0 0',
        height: 46,
        background: 'repeating-linear-gradient(90deg, #4e8735 0 4px, transparent 4px 13px)',
        maskImage: 'linear-gradient(180deg, transparent 0%, #000 60%)',
        WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 60%)',
        animation: 'sway 9s ease-in-out infinite reverse',
        transformOrigin: 'bottom',
      }}
    />
  </div>
);

const cardStyle = {
  position: 'relative',
  zIndex: 2,
  background: '#fff',
  border: '1px solid #e4ece0',
  borderRadius: 18,
  boxShadow: '0 12px 34px -22px rgba(31,64,32,.5)',
};

const columnLabel = {
  fontSize: 12,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: MUTED,
  marginBottom: 4,
};

const ViewerView = () => {
  const [teams, setTeams] = useState([]);
  const [team1, setTeam1] = useState('Team 1');
  const [team2, setTeam2] = useState('Team 2');
  const [ready, setready] = useState();
  const [players, setPlayers] = useState([]);
  const [playersMatch, setPlayersMatch] = useState([]);
  const [match, setMatch] = useState([]);

  // Wer sitzt vor diesem Bildschirm? Es gibt keinen Beamer -- jeder schaut auf
  // seinen eigenen Monitor, also muss diese Seite persoenlich sein.
  const [identity, setIdentity] = useState(null);
  const [lobby, setLobby] = useState({ status: 'closed', claims: [] });
  const [veto, setVeto] = useState(null);
  const [settings, setSettings] = useState({ connectUrl: null });

  // Auswahl (Detail-Panel) und Hover (Tooltip) teilen sich Roster und Tabelle.
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });

  // Verstecke fuer den Otter.
  const rosterRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => onIdentity(setIdentity), []);

  useEffect(() => {
    socket.on('lobby:state', setLobby);
    socket.on('veto:state', setVeto);
    socket.on('settings:state', setSettings);
    socket.on('auth:revoked', () => setIdentity(null));

    return () => {
      socket.off('lobby:state', setLobby);
      socket.off('veto:state', setVeto);
      socket.off('settings:state', setSettings);
      socket.off('auth:revoked');
    };
  }, []);

  // In welchem Team stehe ich? -1 wenn nicht angemeldet oder noch nicht verteilt.
  const myTeamIndex =
    identity && identity.role === 'player' && teams.length
      ? teams.findIndex((team) => team.some((u) => u.steamID === identity.playerId))
      : -1;

  const isMe = (user) => identity && identity.role === 'player' && user.steamID === identity.playerId;

  const isClaimedPlayer = !!(identity && identity.role === 'player');

  // Teams in Echtzeit vom Server empfangen
  useEffect(() => {
    socket.on('updateTeams', (newTeams) => {
      setTeams(newTeams);
    });

    socket.on('updateTeam1', (team1) => {
      setTeam1(team1);
    });

    socket.on('updateTeam2', (team2) => {
      setTeam2(team2);
    });

    socket.on('ready', (ready) => {
      setready(ready);
    });

    socket.on('player_stats', (data) => {
      setPlayers(data);
    });

    socket.on('player_match_stats', (data1) => {
      setPlayersMatch(data1);
    });

    socket.on('match_stats', (data2) => {
      setMatch(data2);
    });

    // Der Socket besteht schon, seit die Seite geladen wurde -- beim Wechsel
    // von /join hierher schickt der Server von sich aus nichts mehr.
    requestState();

    // Cleanup bei Komponenten-Demontage
    return () => {
      socket.off('updateTeams');
      socket.off('updateTeam1');
      socket.off('updateTeam2');
      socket.off('ready');
      socket.off('player_stats');
      socket.off('player_match_stats');
      socket.off('match_stats');
    };
  }, []);

  const list = useMemo(() => enrichPlayers(players, teams), [players, teams]);
  const avg = useMemo(() => averages(list), [list]);
  const detail = useMemo(
    () => (selected ? detailFor(selected, list, avg) : null),
    [selected, list, avg]
  );
  const hoveredPlayer = hovered ? list.find((p) => p.name === hovered) : null;
  const hasStats = useMemo(() => new Set(list.map((p) => p.name)), [list]);

  const myName =
    identity && identity.role === 'player' && teams.length
      ? (teams.flat().find((u) => u.steamID === identity.playerId) || {}).name
      : null;

  const onHover = (name, e) => {
    setHovered(name);
    setTipPos({ x: e.clientX, y: e.clientY });
  };
  const onUnhover = (name) => setHovered((prev) => (prev === name ? null : prev));
  const onSelect = (name) => {
    if (hasStats.has(name)) setSelected(name);
  };

  // Eine Zeile im Team-Kaertchen.
  const rosterEntry = (user, index) => {
    const clickable = hasStats.has(user.name);
    return (
      <div
        key={index}
        className={clickable ? 'otter-hoverable' : undefined}
        onClick={() => onSelect(user.name)}
        onMouseEnter={(e) => clickable && onHover(user.name, e)}
        onMouseLeave={() => onUnhover(user.name)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '7px 10px',
          borderRadius: 12,
          cursor: clickable ? 'pointer' : 'default',
        }}
      >
        <Avatar src={user.imgUrl} initials={initials(user.name)} size={40} font={14} />
        <div>
          <div style={{ fontSize: 15, fontWeight: isMe(user) ? 700 : 400 }}>
            {user.nickName || user.name}
          </div>
          <div style={{ fontSize: 12, color: '#778574' }}>{user.name}</div>
        </div>
      </div>
    );
  };

  const teamColumn = (label, teamName, roster, mine) => (
    <div>
      <div style={columnLabel}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 650, marginBottom: 16 }}>
        {teamName}
        {mine && (
          <span
            style={{
              marginLeft: 8,
              verticalAlign: 'middle',
              borderRadius: 99,
              background: '#dcecd0',
              color: '#33612f',
              padding: '2px 9px',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '.04em',
            }}
          >
            dein Team
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {(roster || []).map(rosterEntry)}
      </div>
    </div>
  );

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        color: '#1b1f1c',
        background: 'linear-gradient(180deg, #FFFFFFEB, #e9f3e2 45%, #d3e8c6 100%)',
        overflow: 'hidden',
      }}
    >
      <Meadow />

      <div
        className="otter-page-inner"
        style={{ position: 'relative', zIndex: 2, maxWidth: 1136, margin: '0 auto', padding: '56px 64px 260px' }}
      >
        {/* Muss hier drin stehen: die Karten-Auftritte rechnen mit offsetTop
            gegen diesen Container. */}
        <OtterPeek anchors={[rosterRef, tableRef]} />

        <div
          style={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            alignItems: 'baseline',
            gap: 14,
            marginBottom: 26,
          }}
        >
          <h1 style={{ margin: 0, fontSize: 30, fontWeight: 650, letterSpacing: '-0.02em' }}>
            Otter-Team View
          </h1>
          <span style={{ fontSize: 13, color: MUTED }}>
            {(match || []).length} matches tracked
          </span>
        </div>

        {identity && identity.role === 'player' && (
          <div
            style={{
              ...cardStyle,
              padding: '12px 18px',
              marginBottom: 18,
              fontSize: 14,
              boxShadow: 'none',
            }}
          >
            Du bist <span style={{ fontWeight: 650 }}>{identity.playerName}</span>
            {myTeamIndex === 0 && (
              <span>
                {' '}- du spielst bei <span style={{ fontWeight: 650 }}>{team1}</span>
              </span>
            )}
            {myTeamIndex === 1 && (
              <span>
                {' '}- du spielst bei <span style={{ fontWeight: 650 }}>{team2}</span>
              </span>
            )}
          </div>
        )}

        {/* Der Hinweis gilt fuer jeden, der noch kein beanspruchter Spieler ist
            -- also auch fuer den Admin. Der oeffnet die Lobby und will von hier
            aus den Link und den Code weitersagen koennen; frueher hing der
            Hinweis an !identity und verschwand, sobald man angemeldet war. */}
        {lobby.status === 'open' && !isClaimedPlayer && (
          <div
            style={{
              ...cardStyle,
              padding: '12px 18px',
              marginBottom: 18,
              fontSize: 14,
              boxShadow: 'none',
            }}
          >
            Die Lobby ist offen -{' '}
            <a style={{ fontWeight: 650, textDecoration: 'underline' }} href="/join">
              hier anmelden
            </a>
            , damit die Seite weiss, wer du bist.
            {/* Den Code bekommt nur der Admin vom Server geschickt. */}
            {lobby.code && (
              <span>
                {' '}Code:{' '}
                <span style={{ fontWeight: 650, letterSpacing: '.08em' }}>{lobby.code}</span>
              </span>
            )}
          </div>
        )}

        <div ref={rosterRef} style={{ ...cardStyle, padding: '26px 28px', marginBottom: 22 }}>
          {teams.length > 0 ? (
            <div
              className="otter-roster"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                gap: 26,
                alignItems: 'start',
              }}
            >
              {teamColumn('Team A', team1, teams[0], myTeamIndex === 0)}
              <div
                className="otter-vs"
                style={{
                  alignSelf: 'center',
                  display: 'grid',
                  placeItems: 'center',
                  width: 54,
                  height: 54,
                  borderRadius: '50%',
                  background: '#1b1f1c',
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 700,
                  letterSpacing: '.04em',
                }}
              >
                VS
              </div>
              {teamColumn('Team B', team2, teams[1], myTeamIndex === 1)}
            </div>
          ) : (
            <div style={{ color: MUTED, fontSize: 14 }}>Warten auf generierte Teams...</div>
          )}
        </div>

        {/* Die Adresse kommt aus den Admin-Einstellungen. Steht dort nichts,
            gibt es nichts zu verbinden -- frueher stand hier eine feste IP,
            die nur ein Rebuild aendern konnte. */}
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: 22,
            transition: 'opacity .8s',
            opacity: ready && settings.connectUrl ? 1 : 0,
            pointerEvents: ready && settings.connectUrl ? 'auto' : 'none',
          }}
        >
          <button
            type="button"
            style={{
              borderRadius: 12,
              background: 'linear-gradient(90deg,#a8cf8d,#4f9153)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              padding: '10px 22px',
              boxShadow: '0 10px 24px -14px rgba(31,64,32,.9)',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.preventDefault();
              if (settings.connectUrl) window.location.href = settings.connectUrl;
            }}
          >
            Connect to server
          </button>
          <p style={{ paddingTop: 8, fontSize: 12, color: '#8b978a' }}>
            {settings.gameHost}:{settings.gamePort}
          </p>
        </div>

        <div ref={tableRef}>
          <DataTable
            list={list}
            matches={match}
            matchDetails={playersMatch}
            selected={selected}
            hovered={hovered}
            onSelect={onSelect}
            onHover={onHover}
            onUnhover={onUnhover}
            meName={myName}
          />
        </div>
      </div>

      <MapVeto veto={veto} identity={identity} teamNames={[team1, team2]} isAdmin={false} />

      <PlayerDetail detail={detail} onClose={() => setSelected(null)} />

      <div
        style={{
          position: 'fixed',
          zIndex: 25,
          pointerEvents: 'none',
          left: tipPos.x + 16,
          top: tipPos.y + 16,
          opacity: hoveredPlayer && !selected ? 1 : 0,
          transition: 'opacity .12s',
        }}
      >
        {hoveredPlayer && (
          <div
            style={{
              background: '#1b241a',
              color: '#eef3ea',
              borderRadius: 12,
              padding: '10px 14px',
              fontSize: 12.5,
              lineHeight: 1.55,
              boxShadow: '0 14px 30px -18px rgba(0,0,0,.8)',
            }}
          >
            <div style={{ fontWeight: 650, marginBottom: 4 }}>{hoveredPlayer.name}</div>
            <div style={{ color: '#b9c9b2' }}>
              K/D {n1(hoveredPlayer.kd)} &middot; {Math.round(hoveredPlayer.dpk)} dmg per kill
            </div>
            <div style={{ color: '#b9c9b2' }}>
              Utility {hoveredPlayer.util} &middot; {n1(hoveredPlayer.utilShare)}% of output
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewerView;
